import axiosInstance from './axiosInstance';
import {
  ICareHome,
  IAddress,
  IManager,
  ICareHomeDayNightPayload,
  ICareHomeListParams,
  ICareHomeListResponse,
  ICareHomeCreatePayload,
} from '../interfaces';
import { ICareHomeStats } from '../interfaces/CareHome/ICareHomeStats.ts';

class CareHomeService {
  private static instance: CareHomeService;
  private constructor() {}

  public static getInstance(): CareHomeService {
    if (!CareHomeService.instance) {
      CareHomeService.instance = new CareHomeService();
    }
    return CareHomeService.instance;
  }

  public async getCareHomes(params?: ICareHomeListParams): Promise<ICareHomeListResponse> {
    try {
      const response = await axiosInstance.get<ICareHomeListResponse>(
        '/carehomes', { params }
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch care homes');
    }
  }

  public async getCareHome(careHomeId: string): Promise<ICareHome> {
    try {
      const response = await axiosInstance.get<ICareHome>(
        `/carehomes/${careHomeId}`
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch care home data');
    }
  }

  public async createCareHome(payload: ICareHomeCreatePayload): Promise<ICareHome> {
    try {
      const response = await axiosInstance.post<ICareHome>(
        '/carehomes',
        payload
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to create care home');
    }
  }

  public async updateCareHomeAddress(
    careHomeId: string,
    address: IAddress
  ): Promise<ICareHome> {
    try {
      const payload = { address };
      const response = await axiosInstance.patch<ICareHome>(
        `/carehomes/${careHomeId}/address`,
        payload
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to update care home address');
    }
  }

  public async updateCareHomeManager(
    careHomeId: string,
    manager: IManager
  ): Promise<ICareHome> {
    try {
      const payload = { manager };
      const response = await axiosInstance.patch<ICareHome>(
        `/carehomes/${careHomeId}/manager`,
        payload
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to update care home manager');
    }
  }

  public async updateCareHomeDayNight(
    careHomeId: string,
    payload: ICareHomeDayNightPayload
  ): Promise<ICareHome> {
    try {
      const response = await axiosInstance.patch<ICareHome>(
        `/carehomes/${careHomeId}/day_night`,
        payload
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to update care home day and night times');
    }
  }

  public async updateCareHomeRegistrationId(
    careHomeId: string,
    registrationId: string | null
  ): Promise<ICareHome> {
    try {
      const payload = { registration_id: registrationId };
      const response = await axiosInstance.patch<ICareHome>(
        `/carehomes/${careHomeId}/registration_id`,
        payload
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to update care home registration ID');
    }
  }

  public async getCareHomeStats(careHomeId: string): Promise<ICareHomeStats> {
    try {
      const response = await axiosInstance.get<ICareHomeStats>(
        `/carehomes/${careHomeId}/stats`
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch care home stats');
    }
  }

  public async updateCareHomeActiveStatus(
    careHomeId: string,
    isActive: boolean
  ): Promise<ICareHome> {
    try {
      const payload = { is_active: isActive };
      const response = await axiosInstance.patch<ICareHome>(
        `/carehomes/${careHomeId}/status`,
        payload
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to update care home active status');
    }
  }
}

export default CareHomeService.getInstance();
